<template>
  <div
    class="edit-user-form"
  >
    <m-content
      padding
      class="_content"
    >
      <h5 class="pa-0">
        {{ $t('editUserForm.groups.personal') }}
      </h5>
      <m-form-item
        :label="$t('editUserForm.firstName')"
      >
        <m-text-field
          v-model:value="firstName"
          autocomplete="off"
          :disabled="!canSave"
        />
      </m-form-item>
      <m-form-item
        :label="$t('editUserForm.lastName')"
      >
        <m-text-field
          v-model:value="lastName"
          autocomplete="off"
          :disabled="!canSave"
        />
      </m-form-item>
      <m-form-item
        :label="$t('editUserForm.email')"
      >
        <m-text-field
          v-model:value="email"
          disabled
        />
      </m-form-item>
      <m-form-item
        :label="$t('editUserForm.language')"
      >
        <m-text-field
          v-model:value="languageLabel"
          disabled
        />
      </m-form-item>
      <m-form-item
        :label="$t('userList.accessGroups')"
      >
        <m-text-field
          :value="user.accessGroups"
          disabled
        >
          <template
            #suffix
          >
            <m-btn
              hide-boder
              xs
              @click="$emit('go-to-access-groups')"
            >
              {{ $t('general.edit') }}
            </m-btn>
          </template>
        </m-text-field>
      </m-form-item>
      <h5 class="pa-0">
        {{ $t('editUserForm.groups.other') }}
      </h5>
      <template
        v-for="prop in userProperties"
        :key="prop.uid"
      >
        <m-form-item
          :label="getFieldLabel(prop)"
        >
          <property-form-item
            :property="prop"
            :property-values="propertyValues"
            full-width
            match-trigger-width
            popup
            wrap
            :disabled="!canSave"
            :placeholder="$t('general.empty')"
            clearable
            @change="updateProperty($event, prop.uid)"
          />
        </m-form-item>
      </template>
    </m-content>
    <m-default-form-actions
      v-if="canSave"
      :show-cancel="showCancel"
      :loading="updateUserLoading"
      clickable
      @cancel="emitCancel"
      @submit="submit"
    />
  </div>
</template>

<script>
import PropertyFormItem from '@/components/PropertyFormItem.vue';
import useAccess from '@/composables/access/access';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useProperties from '@/composables/property/property';
import useSpace from '@/composables/space/space';
import useUsers from '@/composables/user/users';
import { accessGroupFlag } from 'shared/constants.json';
import { copy } from 'shared/lib/copy';
import { email, language } from 'shared/composables/user';
import { platformLanguages } from '@/lib/language';
import { ref } from 'vue';
import { textByLang } from 'shared/lib/language';

export default {
  name: 'EditUserForm',
  props: {
    user: {
      type: Object,
      default: () => null,
    },
    account: {
      type: Object,
      required: true,
    },
    color: {
      type: String,
      default: 'white',
    },
    showCancel: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update', 'cancel', 'update:dirty', 'go-to-access-groups'],
  components: { PropertyFormItem },
  setup() {
    const { userHasRight } = useAccess();
    const { userLang } = useLoggedInUser();
    const { updateUser, updateUserLoading } = useUsers();
    const { userProperties, spaceProperty } = useProperties();
    const spaceSvcTarget = ref(null);
    const spaceSvc = useSpace(spaceSvcTarget);
    return {
      userHasRight,
      updateUser,
      updateUserLoading,
      userProperties,
      spaceProperty,
      userLang,
      spaceSvcTarget,
      spaceSvc,
    };
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      language: '',
      propertyValues: [],
      dirty: false,
    };
  },
  computed: {
    languageLabel() {
      return platformLanguages().find((lang) => lang.code === this.language).name;
    },
    canSave() {
      if (this.user.archivedAt !== null) {
        return false;
      }

      return this.userHasRight([accessGroupFlag.userWriteAccess]);
    },
    values() {
      const userPropIds = this.userProperties.map((p) => p.uid);
      return {
        firstName: this.firstName.trim(),
        lastName: this.lastName.trim(),
        values: this.propertyValues.filter((v) => userPropIds.includes(v.property.uid)).map((v) => ({
          ...v,
          spaces: v.spaces === undefined ? [] : v.spaces.map((o) => ({ uid: o.uid })),
          selectedOptions: v.selectedOptions === undefined ? [] : v.selectedOptions.map((o) => ({ uid: o.uid })),
          users: v.users === undefined ? [] : v.users.map((u) => ({ uid: u.uid })),
          property: { uid: v.property.uid },
        })),
      };
    },
  },
  methods: {
    updateProperty(value, propId) {
      this.$emit('update:dirty', true);
      const index = this.propertyValues.reduce((res, v, i) => {
        if (v.property.uid === propId) {
          res = i;
        }
        return res;
      }, -1);
      if (index === -1) {
        throw new Error(`property not found: ${propId}`);
      }
      this.propertyValues.splice(index, 1, value);
    },
    submit() {
      this.$emit('update:dirty', false);
      this.update({ ...this.user, ...this.values });
    },
    update(user) {
      const toUpdate = user;
      delete (toUpdate.notificationSetting);
      delete (toUpdate.profileImage);

      const removedSpaces = this.findRemovedSpaces(this.user, toUpdate);
      if (removedSpaces.length > 0) {
        removedSpaces.forEach((space) => {
          this.spaceSvcTarget = space;
          const member = this.spaceSvc.members.value.find((member) => member.user.uid === toUpdate.uid);
          if (member.role !== null) {
            this.spaceSvc.replaceRole(member);
          }
        });
        this.spaceSvcTarget = null;
      }

      this.updateUser(toUpdate).then(() => {
        this.$emit('update');
        this.$showSnackbar({ color: 'success', message: this.$t('success.updated') });
      }).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });
    },
    findRemovedSpaces(oldUser, newUser) {
      const oldSpacePV = oldUser.values.find((v) => v.property.uid === this.spaceProperty.uid);
      const newSpacePV = newUser.values.find((v) => v.property.uid === this.spaceProperty.uid);
      const remainingSpacesIDs = newSpacePV.spaces.map(({ uid }) => uid);
      return oldSpacePV.spaces.filter((space) => !remainingSpacesIDs.includes(space.uid));
    },
    getFieldLabel(field) {
      return textByLang(field.label, this.userLang);
    },
    emitCancel() {
      this.$emit('cancel');
    },
  },
  watch: {
    firstName(newVal, oldVal) {
      if (oldVal === '') {
        return;
      }
      this.$emit('update:dirty', true);
    },
    lastName(newVal, oldVal) {
      if (oldVal === '') {
        return;
      }
      this.$emit('update:dirty', true);
    },
  },
  created() {
    const userPropIds = this.userProperties.map((p) => p.uid);
    this.propertyValues = copy(this.user.values.filter((v) => userPropIds.includes(v.property.uid)));
    this.firstName = this.user.firstName;
    this.lastName = this.user.lastName;
    this.language = language(this.user);
    this.email = email(this.user);
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .edit-user-form {
    ._content {
      height: calc(var(--viewport-height-100) - 13rem);
      overflow: auto;
    }
  }

  .edit-user-card {
    width: 100%;
    max-width: $default-card-max-width;
    height: 100%;
  }
</style>
